<template>
  <v-dialog v-model="dialog" max-width="1400px" scrollable>
    <v-card>
      <v-toolbar density="compact" color="indigo" dark>
        <v-toolbar-title class="text-subtitle-1">
          <v-icon size="small">mdi-notebook-outline</v-icon>
          {{ $t('projects.diary.title', { id: projectId }) }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon variant="text" :title="$t('projects.diary.refresh')" @click="load">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn icon variant="text" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="height: 78vh;">
        <div v-if="loading" class="d-flex align-center justify-center" style="height: 100%;">
          <v-progress-circular indeterminate size="28" />
        </div>

        <v-alert v-else-if="error" type="error" variant="tonal" density="comfortable" class="ma-4">
          {{ error }}
        </v-alert>

        <div
          v-else-if="!runs.length"
          class="d-flex flex-column align-center justify-center text-grey"
          style="height: 100%;"
        >
          <v-icon size="48" color="grey">mdi-notebook-remove-outline</v-icon>
          <p class="text-subtitle-1 mt-2">{{ $t('projects.diary.empty') }}</p>
        </div>

        <div v-else class="d-flex" style="height: 100%;">
          <!-- Elenco delle esecuzioni -->
          <div class="diary-list flex-shrink-0">
            <v-list density="compact" nav>
              <v-list-item
                v-for="r in runs" :key="r.runId"
                :active="r.runId === openRunId"
                @click="open(r.runId)"
              >
                <template #prepend>
                  <v-icon size="18" :color="runColor(r)">{{ runIcon(r) }}</v-icon>
                </template>
                <v-list-item-title class="text-body-2">
                  {{ r.verificationId }}<span v-if="r.stepId" class="text-medium-emphasis"> · {{ r.stepId }}</span>
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ fmtDateTime(r.startedAt) }} · {{ duration(r) }}
                  <span v-if="r.costUsd != null"> · {{ fmtUsd(r.costUsd) }}</span>
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>

          <v-divider vertical />

          <!-- Esecuzione aperta -->
          <div class="flex-grow-1 pa-4 diary-detail">
            <div v-if="loadingRun" class="d-flex align-center py-6">
              <v-progress-circular indeterminate size="22" class="mr-3" />
              {{ $t('common.loading') }}
            </div>

            <div v-else-if="!run" class="text-grey text-body-2 py-6">
              {{ $t('projects.diary.pick') }}
            </div>

            <template v-else>
              <!-- Intestazione: cosa era, quanto è costata, com'è finita -->
              <div class="text-caption text-medium-emphasis mb-3">
                {{ run.verificationId }}<span v-if="run.stepId"> · {{ run.stepId }}</span>
                · {{ fmtDateTime(run.startedAt) }}
                <span v-if="run.model"> · {{ run.model }}</span>
                <span v-if="run.requests != null"> · {{ $t('projects.diary.requests', { n: run.requests }) }}</span>
                <span v-if="run.costUsd != null"> · {{ fmtUsd(run.costUsd) }}</span>
                <span v-if="run.userName"> · {{ run.userName }}</span>
              </div>

              <!-- Esecuzione senza riga di consumo: si è fermata prima della fine -->
              <v-alert
                v-if="!run.costUsd && !run.errorMessage && !run.result"
                type="warning" variant="tonal" density="compact" class="mb-3"
              >
                {{ $t('projects.diary.incomplete') }}
              </v-alert>

              <v-alert
                v-if="run.errorMessage"
                type="error" variant="tonal" density="comfortable" class="mb-3"
              >
                {{ run.errorMessage }}
              </v-alert>

              <!-- Il diario, con la stessa forma che ha dal vivo -->
              <div v-if="run.events.length" class="verif-log mb-3">
                <div v-for="(l, i) in run.events" :key="i">
                  <div
                    class="d-flex align-start verif-log-line"
                    :class="{ 'verif-log-clickable': !!l.detail }"
                    @click="l.detail && toggleDetail(i)"
                  >
                    <span class="text-caption text-medium-emphasis mr-2 verif-log-ts">{{ fmtTime(l.at) }}</span>
                    <v-icon size="14" class="mr-1 mt-1" :color="lineColor(l)">{{ lineIcon(l) }}</v-icon>
                    <span class="text-body-2">{{ l.text }}</span>
                    <v-icon
                      v-if="l.detail" size="14" class="ml-1 mt-1" color="grey"
                    >{{ detailOpen.has(i) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </div>
                  <pre v-if="l.detail && detailOpen.has(i)" class="verif-detail">{{ l.detail }}</pre>
                </div>
              </div>

              <!-- Ragionamento e output: lunghi, quindi chiusi finché non servono -->
              <v-expansion-panels v-if="run.reasoning || run.output" multiple class="mb-3">
                <v-expansion-panel v-if="run.reasoning">
                  <v-expansion-panel-title>
                    <v-icon size="16" class="mr-2">mdi-brain</v-icon>
                    {{ $t('projects.verification.reasoning') }}
                    <span class="text-caption text-medium-emphasis ml-2">
                      {{ $t('projects.diary.chars', { n: fmtNum(run.reasoning.length) }) }}
                    </span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <pre class="verif-thinking">{{ run.reasoning }}</pre>
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel v-if="run.output">
                  <v-expansion-panel-title>
                    <v-icon size="16" class="mr-2">mdi-text</v-icon>
                    {{ $t('projects.verification.liveOutput') }}
                    <span class="text-caption text-medium-emphasis ml-2">
                      {{ $t('projects.diary.chars', { n: fmtNum(run.output.length) }) }}
                    </span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <pre class="verif-live">{{ run.output }}</pre>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>

              <!-- La conclusione -->
              <template v-if="run.result">
                <v-divider class="mb-3" />
                <div class="d-flex align-center mb-2">
                  <v-chip
                    v-if="run.result.verdict"
                    :color="verdictColor(run.result.verdict)"
                    label size="small" class="mr-2 text-uppercase"
                  >{{ run.result.verdict }}</v-chip>
                  <v-chip
                    v-if="run.result.canProceed != null"
                    :color="run.result.canProceed ? 'success' : 'error'"
                    label size="small" variant="tonal"
                  >{{ run.result.canProceed ? $t('projects.diary.canProceed') : $t('projects.diary.cannotProceed') }}</v-chip>
                </div>
                <p v-if="run.result.summary" class="text-body-2 mb-3">{{ run.result.summary }}</p>

                <v-list v-if="run.result.findings?.length" density="compact" class="py-0">
                  <v-list-item v-for="(f, i) in run.result.findings" :key="i" class="px-0">
                    <template #prepend>
                      <v-icon size="16" :color="severityColor(f.severity)">{{ severityIcon(f.severity) }}</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">{{ f.field || f.title }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption" style="white-space: normal;">
                      {{ f.message }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </template>
            </template>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiCall } from '../utils/api.js'
import { formatUsdShort } from '../utils/money.js'
import { fmtDateTime, fmtTime, duration, runIcon, runColor } from '../utils/aiRuns.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  projectId: { type: String, default: '' },
  // Esecuzione da aprire per prima: la passa l'elenco trasversale di Gestione IA.
  runId: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

const { t } = useI18n()

const dialog = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

const runs = ref([])
const run = ref(null)
const openRunId = ref(null)
const loading = ref(false)
const loadingRun = ref(false)
const error = ref('')
const detailOpen = ref(new Set())

const fmtUsd = formatUsdShort
const fmtNum = (n) => Number(n || 0).toLocaleString()

function lineIcon(l) {
  if (l.type === 'error') return 'mdi-alert-circle'
  if (l.type === 'result') return 'mdi-flag-checkered'
  return l.type === 'trace' ? 'mdi-information-outline' : 'mdi-chevron-right'
}
function lineColor(l) {
  if (l.type === 'error') return 'error'
  if (l.type === 'result') return 'indigo'
  return l.type === 'trace' ? 'grey' : 'indigo'
}

const verdictColor = (v) => ({ ok: 'success', warning: 'warning', error: 'error' }[v] || 'grey')
const severityColor = (s) => ({ critical: 'error', warning: 'warning', info: 'info' }[s] || 'grey')
const severityIcon = (s) => ({
  critical: 'mdi-alert-octagon', warning: 'mdi-alert', info: 'mdi-information-outline',
}[s] || 'mdi-circle-small')

function toggleDetail(i) {
  const s = new Set(detailOpen.value)
  s.has(i) ? s.delete(i) : s.add(i)
  detailOpen.value = s
}

async function load() {
  if (!props.projectId) return
  loading.value = true
  error.value = ''
  run.value = null
  openRunId.value = null
  try {
    const res = await apiCall(`/api/projects/${encodeURIComponent(props.projectId)}/ai-runs`)
    runs.value = res?.items || []
    // La più recente si apre da sola: è quasi sempre quella che si è venuti a cercare. Quella
    // chiesta si apre anche se è più vecchia delle cento in elenco.
    if (props.runId) await open(props.runId)
    else if (runs.value.length) await open(runs.value[0].runId)
  } catch (e) {
    error.value = e?.message || t('projects.diary.error')
  } finally {
    loading.value = false
  }
}

async function open(runId) {
  openRunId.value = runId
  loadingRun.value = true
  detailOpen.value = new Set()
  try {
    run.value = await apiCall(
      `/api/projects/${encodeURIComponent(props.projectId)}/ai-runs/${runId}`)
  } catch (e) {
    error.value = e?.message || t('projects.diary.error')
  } finally {
    loadingRun.value = false
  }
}

// Si carica all'apertura, non al montaggio: il dialog vive dentro la scheda del processo.
watch(() => props.modelValue, (open) => { if (open) load() })
</script>

<style scoped>
.diary-list {
  width: 340px;
  overflow-y: auto;
}
.diary-detail {
  overflow-y: auto;
}
.verif-log {
  max-height: 46vh;
  overflow-y: auto;
  border-left: 2px solid rgba(63, 81, 181, 0.25);
  padding-left: 8px;
}
.verif-log-ts {
  font-variant-numeric: tabular-nums;
  min-width: 62px;
}
.verif-log-clickable {
  cursor: pointer;
}
.verif-log-clickable:hover {
  background: rgba(63, 81, 181, 0.06);
}
.verif-detail,
.verif-thinking,
.verif-live {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1.45;
  max-height: 40vh;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  padding: 8px;
  margin: 4px 0 8px 0;
}
</style>
